import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { DefaultMatchRequirementMapping } from 'dashboard/models/verification-check';
import {
  VerificationTemplate,
  VerificationTemplateBuilders,
} from 'dashboard/models/verification-template';
import {
  VerificationTemplateVersion,
  VTMPLV_TEMPLATE_TYPE_MAP,
} from 'dashboard/models/verification-template-version';
import {
  setVerificationTemplate,
  setVerificationTemplateVersion,
} from 'dashboard/redux/actions/verification-template';
import {
  setDefaultMatchRequirements,
  setVerificationTemplateBuilders,
  setVerificationTemplates,
  VerificationTemplatesFilterParams,
} from 'dashboard/redux/actions/verification-templates';
import { DashboardThunkAction } from 'dashboard/redux/types';
import {
  ApiEndpoint,
  ApiListResponse,
  ApiModel,
  ApiResource,
  PaginationParams,
} from 'lib/interfaces/api';

interface VerificationTemplateListParams {
  currentPage: number;
  pageSize: number;
  sortParams: SortParams;
  filters?: VerificationTemplatesFilterParams;
  abortSignal?: AbortSignal;
}

type VerificationTemplateAllParams = Omit<VerificationTemplateListParams, 'currentPage'>;

const fetchVerificationTemplatesByPage = async ({
  currentPage,
  pageSize,
  sortParams,
  filters = {},
  abortSignal,
}: VerificationTemplateListParams): Promise<
  ApiListResponse<VerificationTemplate, PaginationParams>
> => {
  const params = createParamsForList(filters, sortParams);
  const paramsFull = {
    ...params,
    page: {
      number: currentPage,
      size: pageSize,
    },
    signal: abortSignal,
  };

  return SessionClientDashboard.listResource<VerificationTemplate, PaginationParams>(
    ApiEndpoint.VerificationTemplate,
    paramsFull,
  );
};

export function loadVerificationTemplates(
  params: Partial<VerificationTemplateAllParams> = {},
): DashboardThunkAction<Promise<void>> {
  return async (dispatch) => {
    const paramsFull: VerificationTemplateListParams = {
      pageSize: 100,
      sortParams: { sort: '-', shouldSortDescending: false },
      ...params,
      currentPage: 1,
    };
    const firstPageResponse = await fetchVerificationTemplatesByPage(paramsFull);
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const totalPages = firstPageResponse.meta['total-pages'];

    let responses: ApiListResponse<VerificationTemplate, PaginationParams>[] = [];
    if (totalPages > 1) {
      const pages = Array.from(Array(totalPages).keys(), (x) => x + 1);
      pages.shift();

      responses = await Promise.all(
        pages.map((page) => {
          return fetchVerificationTemplatesByPage({ ...paramsFull, currentPage: page });
        }),
      );
    }

    const verificationTemplates = [
      ...firstPageResponse.data,
      ...responses.map(({ data }) => data).flat(),
    ];

    dispatch(
      setVerificationTemplates(
        verificationTemplates,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        (responses.length > 0 ? responses[responses.length - 1] : firstPageResponse).meta,
      ),
    );
  };
}

export function loadVerificationTemplatesList(
  params: VerificationTemplateListParams,
): DashboardThunkAction<Promise<void>> {
  return async (dispatch) => {
    const verificationTemplatesResponse = await fetchVerificationTemplatesByPage(params);

    const verificationTemplates = verificationTemplatesResponse.data;
    const paginationParams = verificationTemplatesResponse.meta;

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    dispatch(setVerificationTemplates(verificationTemplates, paginationParams));
  };
}

function createParamsForList(
  verificationTemplateFilters: VerificationTemplatesFilterParams,
  verificationSortParams: SortParams,
) {
  const { createdAtStart, createdAtEnd, status, type, omni } = verificationTemplateFilters;
  const { sort, shouldSortDescending } = verificationSortParams;

  return {
    filter: {
      'created-at-start': createdAtStart?.toString(),
      'created-at-end': createdAtEnd?.toString(),
      status,
      type,
      omni,
    },
    sort: `${shouldSortDescending ? '-' : ''}${sort}`,
  };
}

export function createVerificationTemplate(builder: string): DashboardThunkAction<
  Promise<{
    verificationTemplate: VerificationTemplate;
    verificationTemplateVersion?: VerificationTemplateVersion;
  }>
> {
  return async (dispatch) => {
    const { data: verificationTemplate, included } =
      await SessionClientDashboard.postResourceCreate<
        VerificationTemplate,
        {
          type: ApiModel.VerificationTemplate;
          attributes: { builder: string };
        }
      >({
        type: ApiModel.VerificationTemplate,
        attributes: {
          builder,
        },
      });

    const verificationTemplateVersion = included?.find(
      (entity): entity is VerificationTemplateVersion => isVerificationTemplateVersion(entity),
    );

    dispatch(setVerificationTemplate(verificationTemplate));
    if (verificationTemplateVersion) {
      dispatch(setVerificationTemplateVersion(verificationTemplateVersion));
    }

    // Return both the template and version
    return { verificationTemplate, verificationTemplateVersion };
  };
}

/**
 * helper to check verification template version types when setting
 * vtmpl version in redux after creating vtmpl
 */
export function isVerificationTemplateVersion(
  entity: ApiResource,
): entity is VerificationTemplateVersion {
  return Object.keys(VTMPLV_TEMPLATE_TYPE_MAP).includes(entity.type);
}

export function loadDefaultMatchRequirements(): DashboardThunkAction {
  return async (dispatch) => {
    const response = await SessionClientDashboard.get<DefaultMatchRequirementMapping>(
      ApiEndpoint.VerificationTemplate,
      'default-check-requirements',
      '',
    );

    dispatch(setDefaultMatchRequirements(response));
  };
}

export const loadVerificationTemplateBuilders = (): DashboardThunkAction => async (dispatch) => {
  const response = await SessionClientDashboard.getResourceMeta<VerificationTemplateBuilders>(
    ApiEndpoint.VerificationTemplate,
    'builders',
  );

  const supportedBuilders = response?.meta;
  if (supportedBuilders) {
    dispatch(setVerificationTemplateBuilders(supportedBuilders));
  }
};
